import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit2, Trash2, Sprout as SproutIcon, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { crop: "", unit: "प्रति क्विंटल", price: "", market: "सूरजपुर मंडी" };

export default function MandiPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/mandi").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      if (editId) await api.put(`/mandi/${editId}`, form); else await api.post("/mandi", form);
      toast.success("सहेज दिया"); setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch(err) { toast.error(formatError(err)); }
  };
  const del = async (id) => { if (!window.confirm("हटाएँ?")) return; await api.delete(`/mandi/${id}`); load(); };

  const quickSeed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mandi/quickseed");
      if (data.seeded > 0) toast.success(`${data.seeded} फसलें जोड़ी गयीं — अब आज के दाम edit करें`);
      else toast.info("सभी common crops पहले से मौजूद हैं");
      load();
    } catch(err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };
  const refreshAg = async () => {
    setBusy(true);
    try {
      // Prefer the stored key in site-settings; backend falls back automatically
      const { data } = await api.post(`/mandi/refresh-agmarknet`);
      toast.success(`AgMarknet: ${data.updated} rates अपडेट हुए`); load();
    } catch(err) {
      const msg = formatError(err);
      if (msg.includes("data.gov.in")) toast.error("पहले Site Settings में data.gov.in API key सेव करें");
      else toast.error(msg);
    }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="mandi-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div><h1 className="text-2xl font-bold">मंडी भाव प्रबंधन</h1><p className="text-sm text-slate-500 mt-1">{items.length} फसलें</p></div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={quickSeed} disabled={busy} data-testid="mandi-quickseed"><SproutIcon className="w-4 h-4 mr-1.5"/>Common Crops जोड़ें</Button>
          <Button variant="outline" onClick={refreshAg} disabled={busy} data-testid="mandi-refresh"><RefreshCw className="w-4 h-4 mr-1.5"/>AgMarknet से अपडेट</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setForm(EMPTY); setEditId(null); }} data-testid="add-mandi-btn"><Plus className="w-4 h-4 mr-1.5"/>नया भाव</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>मंडी भाव</DialogTitle><DialogDescription>फसल का दैनिक भाव जोड़ें/अपडेट करें</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label>फसल</Label><Input value={form.crop} onChange={(e) => setForm({...form, crop: e.target.value})} placeholder="धान / गेहूँ / प्याज" data-testid="mandi-crop"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>भाव (₹)</Label><Input value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} placeholder="2100" data-testid="mandi-price"/></div>
                <div><Label>इकाई</Label><Input value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})}/></div>
              </div>
              <div><Label>मंडी</Label><Input value={form.market} onChange={(e) => setForm({...form, market: e.target.value})}/></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>रद्द</Button><Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-mandi">सहेजें</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>फसल</TableHead><TableHead>भाव</TableHead><TableHead>इकाई</TableHead><TableHead>मंडी</TableHead><TableHead>अपडेट</TableHead><TableHead className="text-right">कार्य</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((m) => (
              <TableRow key={m.id}>
                <TableCell className="font-bold">{m.crop}</TableCell>
                <TableCell className="font-mono text-emerald-700 font-bold">₹{m.price}</TableCell>
                <TableCell className="text-xs text-slate-600">{m.unit}</TableCell>
                <TableCell className="text-xs">{m.market}</TableCell>
                <TableCell className="text-xs text-slate-500">{new Date(m.updated_at).toLocaleDateString("hi-IN")}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setForm({crop:m.crop,unit:m.unit,price:m.price,market:m.market}); setEditId(m.id); setOpen(true); }}><Edit2 className="w-4 h-4"/></Button>
                  <Button variant="ghost" size="sm" onClick={() => del(m.id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">कोई मंडी भाव नहीं</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
